import React, { useCallback, useEffect, useState } from 'react';

import { Icons, PageTitle, Select } from '@components/common';
import ProfileTabSection from '@components/common/ProfileTabSection';
import KpiAlertRulesTable from '@components/routes/AdminPanel/KpiAlertsManagement/KpiAlertRulesTable';
import KpiAlertsHistoryTable from '@components/routes/AdminPanel/KpiAlertsManagement/KpiAlertsHistoryTable';

import { useQueryKpiAlertVendors } from '@hooks/kpiAlerts/useQueryKpiAlertVendors';
import { useWindowHeight } from '@hooks/utils/useWindowHeight';

import { TSelectOption } from '@interfaces/components/componentTypes';

import { EAdminPanelPageTitles, TVendorsValues } from '@enums/common';

import { EKpiAlertTab, tabs } from '@components/routes/AdminPanel/KpiAlertsManagement/constants';

import styles from './KpiAlertsManagement.module.scss';

const OFFSET_HEIGHT_RULES = 302;
const OFFSET_HEIGHT_HISTORY = 325;

function KpiAlertsManagement() {
	const [currentTab, setCurrentTab] = useState<EKpiAlertTab>(EKpiAlertTab.rules);
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption<TVendorsValues>>(null);

	const { vendorsOptions, isLoading } = useQueryKpiAlertVendors();

	const isSingleVendor = vendorsOptions.length === 1;

	useEffect(() => {
		if (!selectedVendor) {
			const defaultVendor = vendorsOptions.find((vendor) => vendor?.isDefault) || vendorsOptions[0];

			setSelectedVendor(defaultVendor as TSelectOption<TVendorsValues>);
		}

	}, [vendorsOptions, selectedVendor]);

	const isVendorSelectDisabled = vendorsOptions.length === 1;

	const onVendorChange = useCallback((option: TSelectOption<TVendorsValues>) => {
		setSelectedVendor(option);
	}, []);

	const getCurrentTab = useCallback(() => {
		if (currentTab === EKpiAlertTab.rules) {
			return <KpiAlertRulesTable selectedVendor={selectedVendor} />;
		}

		return <KpiAlertsHistoryTable selectedVendor={selectedVendor} />;
	}, [currentTab, selectedVendor]);

	const handleSetCurrentTab = useCallback((tab: EKpiAlertTab) => setCurrentTab(tab), []);

	const windowHeight = useWindowHeight();

	const [maxHeight, setMaxHeight] = useState('');

	useEffect(() => {
		const offset = isSingleVendor
			? 0
			: currentTab === EKpiAlertTab.rules ? OFFSET_HEIGHT_RULES : OFFSET_HEIGHT_HISTORY;
		const maxHeight = windowHeight - offset;

		setMaxHeight(`${maxHeight}px`);
	}, [windowHeight, currentTab, isSingleVendor]);

	return (
		<div className={styles.KpiNotifications}>
			<PageTitle
				icon={Icons.kpiNotificationsOutline()}
				title={EAdminPanelPageTitles.KPINotifications}
			/>
			<div className={styles.whiteWrapper}>
				{!isSingleVendor && (
					<div className={styles.vendor}>
						<Select
							handleChange={onVendorChange}
							isLoading={isLoading}
							isReadOnly={isVendorSelectDisabled}
							isWithoutUseForms
							label='Vendor'
							name='vendor'
							options={vendorsOptions}
							placeholder='Select vendor'
							value={selectedVendor}
							width='240px'
						/>
					</div>
				)}
				<ProfileTabSection
					currentTab={currentTab}
					getCurrentTab={getCurrentTab}
					isFullHeight
					isWithoutShadow
					maxHeight={maxHeight}
					maxWidth='unset'
					setCurrentTab={handleSetCurrentTab}
					tabWidth='220px'
					tabs={tabs}
				/>
			</div>
		</div>
	);
}

export default KpiAlertsManagement;
